import React, { useState, useEffect } from "react";
import useAxiosInstance from "../../hooks/useAxios";
import { toast } from "react-toastify";
import FormRender from "../../components/FormRender";
import { NEWSLETTER_REVIVER_FORM_CONFIG } from "../../helper";

const Checkbox = ({ label, checked, onChange, id }) => (
    <div className="flex items-center">
        <input id={id} type="checkbox" checked={checked} onChange={onChange} className="h-4 w-4 rounded border-gray-300 text-black focus:ring-black" />
        <label htmlFor={id} className="ml-2 block text-sm font-medium text-gray-800">{label}</label>
    </div>
);

const NewsletterReviver = () => {
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState(1);
    const [responses, setResponses] = useState({});
    const axiosInstance = useAxiosInstance();
    const [allChained, setAllChained] = useState(true);

    const handleAllChainedChange = (e) => {
        setAllChained(e.target.checked);
    };

    const TABS = [
        { num: 1, title: "Initial Tasks" },
        { num: 2, title: "Load News" },
        { num: 3, title: "Synthesize Articles" },
        { num: 4, title: "1-2 Liners" },
        { num: 5, title: "Make Summary" },
        { num: 6, title: "Send Email" },
    ];

    const ENDPOINTS = {
        2: "/task_fire_load_newsletter_news_and_save_tasks/",
        3: "/task_fire_synthesize_best_articles_tasks/",
        4: "/newsletter/v2/fire_or_get_remaining_1_2_line_tasks/",
        5: "/task_fire_task_make_summary_tasks/",
        6: "/task_fire_process_send_email_tasks/",
    };

    const [formData, setFormData] = useState({
        step1_industry_id: "", step1_industry_mode: "count",
        step1_company_id: "", step1_company_mode: "count",
        step2_reference_id: "", step2_task_id: "", step2_task_name: "task_fire_load_newsletter_news_and_save_tasks", step2_mode: "count",
        step3_reference_id: "", step3_task_id: "", step3_task_name: "task_fire_synthesize_best_articles_tasks", step3_mode: "count",
        step4_reference_id: "", step4_task_id: "", step4_task_name: "fire_or_get_remaining_1_2_line_tasks", step4_mode: "count",
        step5_reference_id: "", step5_task_id: "", step5_task_name: "task_fire_task_make_summary_tasks", step5_mode: "count",
        step6_reference_id: "", step6_task_id: "", step6_task_name: "/task_fire_process_send_email_tasks", step6_mode: "count",
    });

    useEffect(() => {
        if (allChained) {
            setFormData(prev => {
                let updated = { ...prev };
                for (let i = 3; i <= 6; i++) {
                    const stepKey = `step${i}`;
                    const prevStep = `step${i - 1}`;
                    updated[`${stepKey}_reference_id`] = updated[`${prevStep}_reference_id`] || "";
                    updated[`${stepKey}_task_id`] = updated[`${prevStep}_task_id`] || (updated[`${stepKey}_reference_id`] ? `production/${updated[`${stepKey}_reference_id`]}/<unique_task_id>.pickle` : "");
                    updated[`${stepKey}_task_name`] = updated[`${prevStep}_task_name`] || "";
                }
                return updated;
            });
        }
    }, [allChained, formData.step2_reference_id, formData.step2_task_id, formData.step2_task_name, formData.step3_reference_id, formData.step4_reference_id, formData.step5_reference_id]);

    const handleApiCall = async (step, stepName, endpoint, payload, requiredFields, mode) => {
        if (requiredFields && !requiredFields.every(field => field)) {
            toast.error(`Please fill all required fields for ${stepName}.`);
            return;
        }
        setLoading(true);
        setResponses(prev => ({ ...prev, [step]: undefined }));
        try {
            const res = await axiosInstance.post(endpoint, payload);
            setResponses(prev => ({
                ...prev,
                [step]: { title: `${stepName} - Success`, data: res.data }
            }));
            if (mode === 'count') {
                const count = res.data?.data?.count ?? res.data?.count ?? 'an unknown number of';
                toast.success(`${stepName}: Found ${count} items.`);
            } else {
                toast.success(`${stepName} processed successfully.`);
            }
        } catch (err) {
            const errorMessage = err.response?.data?.message || `An error occurred during ${stepName}.`;
            setResponses(prev => ({
                ...prev,
                [step]: { title: `${stepName} - Failed`, data: { error: errorMessage, details: err.response?.data } }
            }));
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleStep1Submit = async () => {
        const { step1_industry_id, step1_industry_mode, step1_company_id, step1_company_mode } = formData;
        if (!step1_industry_id && !step1_company_id) {
            toast.error("Please provide at least one Reference ID for Step 1.");
            return;
        }
        setLoading(true);
        setResponses(prev => ({ ...prev, 1: undefined }));
        try {
            const industryPromise = step1_industry_id
                ? axiosInstance.post("/newsletter/v2/fire_or_get_remaining_industry_tasks/", { reference_id: Number(step1_industry_id), mode: step1_industry_mode })
                : Promise.resolve(null);
            const companyPromise = step1_company_id
                ? axiosInstance.post("/newsletter/v2/fire_or_get_remaining_company_tasks/", { reference_id: Number(step1_company_id), mode: step1_company_mode })
                : Promise.resolve(null);
            const [industryResult, companyResult] = await Promise.allSettled([industryPromise, companyPromise]);
            const responseData = {};
            if (industryResult.status === 'fulfilled' && industryResult.value) responseData.industry = industryResult.value.data;
            if (companyResult.status === 'fulfilled' && companyResult.value) responseData.company = companyResult.value.data;
            if (industryResult.status === 'rejected') responseData.industry = { error: industryResult.reason.response?.data?.message || "Industry request failed." };
            if (companyResult.status === 'rejected') responseData.company = { error: companyResult.reason.response?.data?.message || "Company request failed." };
            toast.success("Step 1 processing complete. Check results below.");
            setResponses(prev => ({
                ...prev,
                1: { title: "Step 1 - Results", data: responseData }
            }));
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = () => {
        if (activeTab === 1) {
            handleStep1Submit();
            return;
        }
        const stepKey = `step${activeTab}`;
        const refId = formData[`${stepKey}_reference_id`];
        const taskId = formData[`${stepKey}_task_id`];
        const taskName = formData[`${stepKey}_task_name`];
        const mode = formData[`${stepKey}_mode`];
        const payload = {
            reference_id: Number(refId),
            task_id: taskId,
            task_name: taskName,
            mode: mode
        };
        const endpoint = ENDPOINTS[activeTab];
        if (!endpoint) {
            toast.error("Invalid step selected.");
            return;
        }
        handleApiCall(
            activeTab,
            `Step ${activeTab}`,
            endpoint,
            payload,
            [refId, taskId],
            mode
        );
    };

    const getDynamicFormSettings = (stepKey) => {
        const ID_FIELDS = ['reference_id', 'task_id', 'task_name'].flatMap(field => [3, 4, 5, 6].map(num => `step${num}_${field}`));
        return NEWSLETTER_REVIVER_FORM_CONFIG[stepKey].map(setting => ({
            ...setting,
            disabled: allChained && ID_FIELDS.includes(setting.saveKey)
        }));
    };

    return (
        <div className="p-6 bg-white text-black rounded-lg shadow-md w-full">
            <div className="text-3xl font-bold mb-6 border-b pb-4">Newsletter Reviver</div>
            <div className="flex border-b mb-6 overflow-x-auto">
                {TABS.map(tab => (
                    <button
                        key={tab.num}
                        onClick={() => setActiveTab(tab.num)}
                        className={`flex-shrink-0 px-4 py-3 font-semibold text-sm transition-colors duration-200 ${activeTab === tab.num ? "border-b-2 border-black text-black" : "text-gray-500 hover:text-black"}`}
                    >
                        {`Step ${tab.num}: ${tab.title}`}
                    </button>
                ))}
            </div>
            <div className="p-4 border rounded-lg min-h-[250px] bg-gray-50">
                <div className="space-y-6">
                    <h3 className="text-lg font-semibold">{`Step ${activeTab}: ${TABS[activeTab - 1].title}`}</h3>
                     {activeTab > 2 && (
                        <Checkbox
                            id="chainAllSteps"
                            label="Use details from previous step for ALL steps"
                            checked={allChained}
                            onChange={handleAllChainedChange}
                        />
                    )}
                    <FormRender
                        formData={formData}
                        setFormData={setFormData}
                        formSettings={getDynamicFormSettings(`step${activeTab}`)}
                    />
                    <div className="flex justify-end pt-4">
                        <button
                            onClick={handleSubmit}
                            disabled={loading}
                            className="px-5 py-2 bg-black text-white font-semibold rounded-lg hover:bg-gray-800 disabled:bg-gray-400"
                        >
                            {loading ? "Processing..." : `Process Step ${activeTab}`}
                        </button>
                    </div>
                </div>
            </div>
            {responses[activeTab] && (
                <div className="mt-6">
                    <h2 className="text-xl font-semibold mb-2">{responses[activeTab].title}</h2>
                    <div className="p-4 bg-gray-100 rounded-lg max-h-96 overflow-y-auto">
                        <pre className="text-sm whitespace-pre-wrap break-all">{JSON.stringify(responses[activeTab].data, null, 2)}</pre>
                    </div>
                </div>
            )}
        </div>
    );
};

export default NewsletterReviver;